import { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import NoneAnchor from 'components/NoneAnchor'
import { BaseCell } from 'features/dashboard/table/column/DashboardCells'
import { useDashboardTableSelector } from 'features/dashboard/utils/hooks/useDashboardTableSelector'
import Loading from 'components/loading/Loading'
import { showPreview } from 'features/dashboard/operation/keywordExposurePreview/keywordExposurePreviewSlice'
import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux'
import { isPresent } from 'utils'
import { SiteText } from 'features/shared/SiteUrlLink'
import { showKeywordBiddingStrategyEdit } from 'features/dashboard/operation/keywordBiddingStrategyEdit/keywordBiddingStrategyEditModalSlice'
import {
  BiddingStrategyTypes,
  CampaignTypes,
  DashboardInfoTypes,
} from 'features/Codes'
import TextInput from 'components/form/TextInput'
import CurrencyInput from 'components/form/CurrencyInput'
import { setBiddingAmount } from 'features/dashboard/operation/keywordBiddingStrategyEdit/keywordsBiddingStrategyEditSlice'
import ErrorText from 'components/form/ErrorText'
import NullableNumber from 'features/dashboard/table/shared/NullableNumber'
import classNames from 'classnames'
import { fetchDashboardInfoKeyword } from 'features/dashboard/info/dashboardInfoSlice'
import LandingUrlEditLink from 'features/dashboard/operation/keywordLandingUrlEdit/LandingUrlEditLink'
import {
  addActives,
  dashboardTreeActions,
} from 'features/dashboard/tree/dashboardTreeSlice'
import { debounce } from 'lodash'
import {
  setMobileURL,
  setPcURL,
  setUnifiedURL,
} from '../../../operation/keywordLandingUrlEdit/keywordLandingUrlEditSlice'

const delay = 300
export const KeywordNameCell = ({
  value,
  row: {
    original: {
      deleted,
      id,
      parentDomainInfo: {
        campaign: { id: campaignId },
        adGroup: { id: adGroupId },
      },
    },
  },
}) => {
  const dispatch = useDispatch()
  const {
    visible,
    type,
    id: keywordId,
  } = useSelector(state => state.dashboard.info)
  return (
    <BaseCell>
      <span className="thumb_link">
        {value}
        {!deleted && visible && (
          <div className="group_btn">
            <span
              className={classNames('link_chart', {
                on: type === DashboardInfoTypes.KEYWORD && keywordId === id,
                show: type === DashboardInfoTypes.KEYWORD && keywordId === id,
              })}>
              <NoneAnchor
                className="link_g"
                onClick={() => {
                  batch(() => {
                    dispatch(dashboardTreeActions.setAdgroupId(adGroupId))
                    dispatch(addActives(campaignId))
                    dispatch(
                      fetchDashboardInfoKeyword({
                        id,
                        showError: true,
                      })
                    )
                  })
                }}>
                <span className="ico_comm ico_chart">키워드 정보</span>
              </NoneAnchor>
            </span>
          </div>
        )}
      </span>
    </BaseCell>
  )
}
KeywordNameCell.propTypes = {
  value: PropTypes.string,
  row: PropTypes.shape({
    original: PropTypes.shape({
      deleted: PropTypes.bool,
      id: PropTypes.string,
      parentDomainInfo: PropTypes.shape({
        campaign: PropTypes.shape({
          id: PropTypes.string,
        }),
        adGroup: PropTypes.shape({
          id: PropTypes.string,
        }),
      }),
    }),
  }),
}

export const KeywordBiddingAmountCell = ({
  row: {
    original: {
      id,
      keywordType,
      deleted,
      text,
      biddingStrategy,
      parentDomainInfo: {
        adGroup: { maxBiddingAmount: adGroupMaxBiddingAmount },
      },
    },
  },
}) => {
  const dispatch = useDispatch()
  const { editable } = useSelector(state => state.dashboard.table.keywordTable)
  const disabled = useMemo(() => editable || deleted, [editable, deleted])
  const handleClick = () => {
    dispatch(
      showKeywordBiddingStrategyEdit([
        {
          id,
          text,
          biddingStrategy,
          adGroupMaxBiddingAmount,
        },
      ])
    )
  }
  const { maxBiddingAmount } = biddingStrategy
  return (
    <BaseCell>
      {keywordType === CampaignTypes.TALK_CHANNEL ? (
        <span className="align_r">-</span>
      ) : disabled ? (
        <span className="align_r">
          {!isPresent(maxBiddingAmount) && <em className="fc_black">[기본]</em>}
          <NullableNumber value={maxBiddingAmount || adGroupMaxBiddingAmount} />
        </span>
      ) : (
        <NoneAnchor className="align_r fc_emph" onClick={handleClick}>
          {!isPresent(maxBiddingAmount) && <em className="fc_black">[기본]</em>}
          <NullableNumber value={maxBiddingAmount || adGroupMaxBiddingAmount} />
        </NoneAnchor>
      )}
    </BaseCell>
  )
}
KeywordBiddingAmountCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.string.isRequired,
      keywordType: PropTypes.oneOf([
        CampaignTypes.PREMIUM_LINK,
        CampaignTypes.TALK_CHANNEL,
      ]),
      deleted: PropTypes.bool,
      text: PropTypes.string,
      biddingStrategy: PropTypes.shape({
        maxBiddingAmount: PropTypes.number,
      }),
      parentDomainInfo: PropTypes.shape({
        adGroup: PropTypes.shape({
          maxBiddingAmount: PropTypes.number.isRequired,
        }),
      }),
    }),
  }),
}

export const KeywordBiddingAmountEditCell = ({
  row: {
    original: {
      id,
      parentDomainInfo: {
        adGroup: { maxBiddingAmount: adGroupMaxBiddingAmount },
      },
    },
  },
}) => {
  const dispatch = useDispatch()
  const { biddingStrategyError, biddingStrategyWarning, strategy } =
    useSelector(
      ({
        dashboard: {
          operation: { keywordBiddingStrategy },
        },
      }) => ({
        biddingStrategyError: keywordBiddingStrategy.errors[id],
        biddingStrategyWarning: keywordBiddingStrategy.warnings[id],
        strategy: keywordBiddingStrategy.strategies.find(
          s => s.keywordId === id
        ),
      }),
      shallowEqual
    )
  return (
    <BaseCell>
      <div className="box_bidding">
        {strategy?.biddingStrategyType ===
        BiddingStrategyTypes.INHERIT_FROM_ADGROUP ? (
          <TextInput
            value={`[기본]${adGroupMaxBiddingAmount.toLocaleString()}`}
            disabled
          />
        ) : (
          <CurrencyInput
            type="원"
            min={70}
            max={100000}
            step={10}
            maxLength={6}
            onValueChange={v => {
              dispatch(
                setBiddingAmount({
                  id,
                  biddingAmount: v,
                })
              )
            }}
            value={strategy?.maxBiddingAmount || ''}
            error={biddingStrategyError || biddingStrategyWarning}
          />
        )}
      </div>
      <ErrorText error={biddingStrategyError || biddingStrategyWarning} />
    </BaseCell>
  )
}
KeywordBiddingAmountEditCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.string,
      parentDomainInfo: PropTypes.shape({
        adGroup: PropTypes.shape({
          maxBiddingAmount: PropTypes.number.isRequired,
        }),
      }),
    }),
  }),
}

export const QiCell = ({
  row: {
    original: { id, keywordType },
  },
}) => {
  const { qis, qiLoading } = useDashboardTableSelector()
  const qi = qis[id] || 0
  const integerSlotFillingPercent = (qi * 100) / 7
  return (
    <BaseCell center>
      {keywordType === CampaignTypes.TALK_CHANNEL ? (
        '-'
      ) : (
        <>
          {qiLoading ? (
            <Loading size="icon" />
          ) : (
            <div className="ad_grade">
              <span className="bg_grade">
                <span
                  className="bg_grade inner_grade"
                  style={{ width: `${integerSlotFillingPercent}%` }}>
                  {qi}%
                </span>
              </span>
            </div>
          )}
        </>
      )}
    </BaseCell>
  )
}
QiCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.string,
      keywordType: PropTypes.oneOf([
        CampaignTypes.PREMIUM_LINK,
        CampaignTypes.TALK_CHANNEL,
      ]),
    }),
  }),
}

export const ExposureStatusCell = ({
  row: {
    original: { text, deleted, keywordType },
  },
}) => {
  const dispatch = useDispatch()
  return (
    <BaseCell>
      {keywordType === CampaignTypes.TALK_CHANNEL ? (
        '-'
      ) : (
        <>
          {deleted ? (
            <span className="fc_emph">보기</span>
          ) : (
            <NoneAnchor
              className="fc_emph"
              onClick={() => dispatch(showPreview(text))}>
              보기
            </NoneAnchor>
          )}
        </>
      )}
    </BaseCell>
  )
}
ExposureStatusCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      keywordType: PropTypes.oneOf([
        CampaignTypes.PREMIUM_LINK,
        CampaignTypes.TALK_CHANNEL,
      ]),
      text: PropTypes.string,
      deleted: PropTypes.bool,
    }),
  }),
}

export const LandingUrlSettingCell = ({
  row: {
    original: {
      id,
      keywordType,
      deleted,
      text,
      landingUrl,
      parentDomainInfo: {
        bizChannel: { id: bizChannelId },
      },
      landingUrlReviewStatus,
    },
  },
}) => {
  const { editable } = useSelector(state => state.dashboard.table.keywordTable)
  const disabled = useMemo(() => editable || deleted, [editable, deleted])
  return (
    <BaseCell>
      {keywordType === CampaignTypes.TALK_CHANNEL ? (
        '-'
      ) : disabled ? (
        <>{isPresent(landingUrl) ? '설정' : '미설정'}</>
      ) : (
        <LandingUrlEditLink
          id={id}
          text={text}
          bizChannelId={bizChannelId}
          landingUrl={landingUrl}
          landingUrlReviewStatus={landingUrlReviewStatus}
          disabled={deleted}>
          {isPresent(landingUrl) ? '설정' : '미설정'}
        </LandingUrlEditLink>
      )}
    </BaseCell>
  )
}
LandingUrlSettingCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.string,
      keywordType: PropTypes.oneOf([
        CampaignTypes.PREMIUM_LINK,
        CampaignTypes.TALK_CHANNEL,
      ]),
      deleted: PropTypes.bool,
      text: PropTypes.string,
      landingUrl: PropTypes.object,
      landingUrlReviewStatus: PropTypes.string,
      parentDomainInfo: PropTypes.shape({
        bizChannel: PropTypes.shape({
          id: PropTypes.string,
        }),
      }),
    }),
  }),
}

export const LandingUrlCell = ({
  row: {
    original: {
      id,
      keywordType,
      deleted,
      text,
      landingUrl,
      parentDomainInfo: {
        bizChannel: { id: bizChannelId },
      },
      landingUrlReviewStatus,
    },
  },
}) => {
  const { editable } = useSelector(state => state.dashboard.table.keywordTable)
  const disabled = useMemo(() => editable || deleted, [editable, deleted])
  return (
    <BaseCell>
      {keywordType === CampaignTypes.TALK_CHANNEL ? (
        '-'
      ) : (
        <>
          {isPresent(landingUrl) ? (
            <>
              {landingUrl.individualSettingYn === 'Y' ? (
                <>
                  {landingUrl.mobileLandingUrl && (
                    <div className="box_url url_view">
                      <strong className="tit_url">모바일</strong>
                      <div className="txt_url">
                        <LandingUrlEditLink
                          id={id}
                          text={text}
                          bizChannelId={bizChannelId}
                          landingUrl={landingUrl}
                          landingUrlReviewStatus={landingUrlReviewStatus}
                          disabled={disabled}>
                          <SiteText siteUrl={landingUrl.mobileLandingUrl} />
                        </LandingUrlEditLink>
                      </div>
                    </div>
                  )}
                  {landingUrl.pcLandingUrl && (
                    <div className="box_url url_view">
                      <strong className="tit_url">PC</strong>
                      <div className="txt_url">
                        <LandingUrlEditLink
                          id={id}
                          text={text}
                          bizChannelId={bizChannelId}
                          landingUrl={landingUrl}
                          landingUrlReviewStatus={landingUrlReviewStatus}
                          disabled={disabled}>
                          <SiteText siteUrl={landingUrl.pcLandingUrl} />
                        </LandingUrlEditLink>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="box_url url_view">
                  <strong className="tit_url">모바일/PC</strong>
                  <div className="txt_url">
                    <LandingUrlEditLink
                      id={id}
                      text={text}
                      bizChannelId={bizChannelId}
                      landingUrl={landingUrl}
                      landingUrlReviewStatus={landingUrlReviewStatus}
                      disabled={disabled}>
                      <SiteText siteUrl={landingUrl.pcLandingUrl} />
                    </LandingUrlEditLink>
                  </div>
                </div>
              )}
            </>
          ) : (
            <LandingUrlEditLink
              id={id}
              text={text}
              bizChannelId={bizChannelId}
              landingUrl={landingUrl}
              landingUrlReviewStatus={landingUrlReviewStatus}
              disabled={disabled}>
              미설정
            </LandingUrlEditLink>
          )}
        </>
      )}
    </BaseCell>
  )
}
LandingUrlCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.string,
      keywordType: PropTypes.oneOf([
        CampaignTypes.PREMIUM_LINK,
        CampaignTypes.TALK_CHANNEL,
      ]),
      deleted: PropTypes.bool,
      text: PropTypes.string,
      landingUrl: PropTypes.object,
      landingUrlReviewStatus: PropTypes.string,
      parentDomainInfo: PropTypes.shape({
        bizChannel: PropTypes.shape({
          id: PropTypes.string,
        }),
      }),
    }),
  }),
}

export const LandingUrlEditCell = ({
  row: {
    original: { id },
  },
}) => {
  const dispatch = useDispatch()
  const { data, error, siteSetting, isIndividual, hasLandingURL } = useSelector(
    ({
      dashboard: {
        operation: { keywordLandingURL },
      },
    }) => ({
      data: keywordLandingURL.keywords[id],
      error: keywordLandingURL.errors[id],
      siteSetting: keywordLandingURL.siteSetting,
      isIndividual: keywordLandingURL.isIndividual,
      hasLandingURL: keywordLandingURL.hasLandingURL,
    }),
    shallowEqual
  )
  const debouncedUnifiedChange = useMemo(
    () =>
      debounce(value => {
        dispatch(
          setUnifiedURL({
            id,
            landingUrl: value,
          })
        )
      }, delay),
    [dispatch, id]
  )
  const debouncedPcChange = useMemo(
    () =>
      debounce(value => {
        dispatch(
          setPcURL({
            id,
            landingUrl: value,
          })
        )
      }, delay),
    [dispatch, id]
  )
  const debouncedMobileChange = useMemo(
    () =>
      debounce(value => {
        dispatch(
          setMobileURL({
            id,
            landingUrl: value,
          })
        )
      }, delay),
    [dispatch, id]
  )
  const mobileDisabled = useMemo(
    () => siteSetting.isIndividual && !siteSetting.hasMobileSite,
    [siteSetting]
  )
  const pcDisabled = useMemo(
    () => siteSetting.isIndividual && !siteSetting.hasPcSite,
    [siteSetting]
  )
  const [mobileSiteValue, setMobileSiteValue] = useState(
    mobileDisabled ? '미설정' : data.mobileSite || ''
  )
  const [pcSiteValue, setPcSiteValue] = useState(
    pcDisabled ? '미설정' : data.pcSite || ''
  )
  const [unifiedSiteValue, setUnifiedSiteValue] = useState(
    data.unifiedSite || ''
  )
  const handleMobileChange = useCallback(
    value => {
      setMobileSiteValue(value)
      debouncedMobileChange(value)
    },
    [debouncedMobileChange]
  )
  const handlePcChange = useCallback(
    value => {
      setPcSiteValue(value)
      debouncedPcChange(value)
    },
    [debouncedPcChange]
  )
  const handleUnifiedChange = useCallback(
    value => {
      setUnifiedSiteValue(value)
      debouncedUnifiedChange(value)
    },
    [debouncedUnifiedChange]
  )
  useEffect(() => {
    setMobileSiteValue(mobileDisabled ? '미설정' : data.mobileSite || '')
    setPcSiteValue(pcDisabled ? '미설정' : data.pcSite || '')
    setUnifiedSiteValue(data.unifiedSite || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIndividual, mobileDisabled, pcDisabled])
  return (
    <BaseCell>
      {hasLandingURL ? (
        isIndividual ? (
          <>
            <div className="box_url">
              <strong className="tit_url">모바일</strong>
              <div className="txt_url">
                <TextInput
                  placeholder="http:// 또는 https:// 형식의 정상적인 랜딩 URL을 입력하세요."
                  value={mobileSiteValue}
                  disabled={mobileDisabled}
                  error={error?.mobileSite}
                  onValueChange={handleMobileChange}
                  focusOnError
                />
                <ErrorText tag="p" error={error?.mobileSite} />
              </div>
            </div>
            <div className="box_url">
              <strong className="tit_url">PC</strong>
              <div className="txt_url">
                <TextInput
                  placeholder="http:// 또는 https:// 형식의 정상적인 랜딩 URL을 입력하세요."
                  value={pcSiteValue}
                  disabled={pcDisabled}
                  error={error?.pcSite}
                  onValueChange={handlePcChange}
                  focusOnError
                />
                <ErrorText tag="p" error={error?.pcSite} />
              </div>
            </div>
          </>
        ) : (
          <div className="box_url">
            <strong className="tit_url">모바일/PC</strong>
            <div className="txt_url">
              <TextInput
                placeholder="http:// 또는 https:// 형식의 정상적인 랜딩 URL을 입력하세요."
                value={unifiedSiteValue}
                error={error?.unifiedSite}
                onValueChange={handleUnifiedChange}
                focusOnError
              />
              <ErrorText tag="p" error={error?.unifiedSite} />
            </div>
          </div>
        )
      ) : (
        '미설정'
      )}
    </BaseCell>
  )
}
LandingUrlEditCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
}
